// "Choose your password" (admin PR 3a): after logging in with the temporary password
// an admin gave. Same rules and strength meter as the reset screen; the server decides.
// Admins then go on to their two-step step (the answer says which).

import { useState, type FormEvent } from 'react';
import { ApiError, firstPassword, type LoginResult } from '../api';
import { newPasswordProblem } from '../password';
import { PasswordField } from './PasswordField';
import { StrengthMeter } from './StrengthMeter';

export function ChoosePassword({
  ticket,
  email,
  onNext,
  onOver,
}: {
  ticket: string;
  email: string;
  /** the login goes on: a session, or the admin's two-step step */
  onNext: (r: LoginResult) => void;
  /** the ticket is gone (too slow, expired temporary password): back to the login form */
  onOver: (message?: string) => void;
}) {
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problem = newPasswordProblem(password, repeat);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onNext(await firstPassword(ticket, password));
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ticket_expired') {
        onOver(err.message);
        return;
      }
      setError(err instanceof ApiError ? err.message : 'Couldn’t save the password. Try again.');
      setBusy(false);
    }
  }

  return (
    <form className="login-card-body" onSubmit={submit} noValidate>
      <h1>Choose your password</h1>
      <p className="login-notice">
        You logged in with a temporary password from your admin. Choose a password of your own for <span className="login-email">{email}</span>.
        Only you will know it.
      </p>
      {/* lets a password manager save the new password for the right account */}
      <input type="email" name="email" autoComplete="username" value={email} readOnly hidden />
      <PasswordField label="New password" name="new-password" autoComplete="new-password" value={password} onChange={setPassword} autoFocus required>
        <StrengthMeter password={password} email={email} />
      </PasswordField>
      <PasswordField label="Type it again" name="new-password-repeat" autoComplete="new-password" value={repeat} onChange={setRepeat} required />
      {error && (
        <p className="login-error" role="alert">
          {error}
        </p>
      )}
      <button className="btn btn-primary btn-block" type="submit" disabled={busy || !password || !repeat}>
        {busy ? 'Saving…' : 'Save and continue'}
      </button>
      <button className="link-btn login-back" type="button" onClick={() => onOver()}>
        Back to log in
      </button>
    </form>
  );
}
